import React, { useEffect, useState } from "react";
import {
  FiBarChart2,
  FiCheckSquare,
  FiClock,
  FiUsers,
  FiX,
  FiMessageCircle,
  FiMessageSquare,
  FiFileText,
  FiLogIn,
  FiClipboard,
} from "react-icons/fi";
import { Avatar, Button, Divider } from "@heroui/react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRightEndOnRectangleIcon } from "@heroicons/react/24/outline";
import ConfirmationModal from "@/components/modal/ConfirmationModal";
import { CohortService } from "@/services/cohortService";
import { useCohortChatThreads } from "@/hooks/useChat";
import { ROLES } from "@/constants/roles";

const CohortSidebar = () => {
  const { logout, currentUser } = useAuth();
  const location = useLocation();
  const { cohortId } = useParams<{ cohortId: string }>();

  const [cohortName, setCohortName] = useState("[Cohort Name]");
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  // Chat activity across the cohort — badge on "Messages". Red = participant
  // replies we haven't read; amber = our sent messages they haven't opened.
  const { totalUnread, totalAwaitingRead } = useCohortChatThreads(cohortId);

  const sidebarLinks: Array<{
    id: number;
    name: string;
    path: string;
    icon: React.ReactNode;
    badge?: number;
    badgeColor?: "red" | "amber";
    badgeTitle?: string;
  }> = [
    {
      id: 1,
      name: "Cohort Dashboard",
      path: `/cohort-dashboard/${cohortId}`,
      icon: <FiBarChart2 />,
    },
    {
      id: 2,
      name: "Participant Progress",
      path: `/cohort-dashboard/${cohortId}/participant-progress`,
      icon: <FiClock />,
    },
    {
      id: 3,
      name: "Participant Profiles",
      path: `/cohort-dashboard/${cohortId}/participant-profiles`,
      icon: <FiUsers />,
    },
    {
      id: 4,
      name: "Transactions",
      path: `/cohort-dashboard/${cohortId}/transactions`,
      icon: <FiFileText />,
    },
    {
      id: 5,
      name: "Messages",
      path: `/cohort-dashboard/${cohortId}/messages`,
      icon: <FiMessageSquare />,
      badge: totalUnread > 0 ? totalUnread : totalAwaitingRead,
      badgeColor: totalUnread > 0 ? "red" : "amber",
      badgeTitle:
        totalUnread > 0
          ? `${totalUnread} unread message${totalUnread === 1 ? "" : "s"} from participants`
          : `${totalAwaitingRead} sent message${totalAwaitingRead === 1 ? "" : "s"} not read by participants yet`,
    },
    {
      id: 9,
      name: "Trainer Feedback",
      path: `/cohort-dashboard/${cohortId}/feedback`,
      icon: <FiMessageCircle />,
    },
    {
      id: 6,
      name: "Attendance",
      path: `/cohort-dashboard/${cohortId}/attendance`,
      icon: <FiCheckSquare />,
    },
    {
      id: 7,
      name: "Assessment Report",
      path: `/cohort-dashboard/${cohortId}/report`,
      icon: <FiClipboard />,
    },
    // Joining the course as a participant is a trainer-only action.
    ...(currentUser?.role === ROLES.TRAINER
      ? [
          {
            id: 8,
            name: "Join Course",
            path: `/course/${cohortId}`,
            icon: <FiLogIn />,
          },
        ]
      : []),
  ];

  useEffect(() => {
    const fetchCohortName = async () => {
      if (!cohortId) {
        setCohortName("[Cohort Name]");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const cohortData = await CohortService.getCohortById(cohortId);
        if (cohortData?.name) {
          setCohortName(cohortData.name);
        } else {
          setCohortName("[Cohort Name]");
        }
      } catch (error) {
        console.error("Error fetching cohort name:", error);
        setCohortName("[Cohort Name]");
      } finally {
        setLoading(false);
      }
    };

    fetchCohortName();
  }, [cohortId]);

  return (
    <>
      <div
        className={`fixed top-0 z-40 flex flex-col justify-between h-full shadow-md overflow-y-auto overflow-x-hidden transition-all duration-300 ease-in-out bg-[#F5FBFF] ${
          isHovered ? "w-[280px] p-6" : "w-[72px] p-3"
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div>
          <div className="flex justify-center items-center mb-4">
            {isHovered ? (
              <img
                src="/assets/images/logo/logo.png"
                alt="AME Logo"
                className="h-11 w-auto"
              />
            ) : (
              <img
                src="/assets/images/logo/logo.png"
                alt="AME Logo"
                className="h-8 w-8 object-contain"
              />
            )}
          </div>
          <Divider className="h-0.5 bg-[#0071A5]/10" />
          {isHovered && (
            <>
              <input
                type="text"
                value={cohortName}
                onChange={(e) => setCohortName(e.target.value)}
                className="my-4 text-base font-bold leading-none text-[#0071A5] border-none p-2 rounded w-full focus:outline-none focus:ring-2 focus:ring-[#0071A5]/30 bg-transparent"
                placeholder="Enter Cohort Name"
                disabled={loading}
              />
              <Divider className="h-0.5 bg-[#0071A5]/10" />
            </>
          )}
          <div className="flex flex-col gap-1.5 py-4">
            {sidebarLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.id}
                  to={link.path}
                  className={`flex items-center rounded-xl transition-colors whitespace-nowrap ${
                    isHovered ? "px-4 py-3" : "px-3 py-3 justify-center"
                  } ${
                    isActive
                      ? "bg-[#0071A5]/10 text-[#0071A5] font-semibold"
                      : "text-[#0071A5]/70 hover:bg-[#0071A5]/5 hover:text-[#0071A5]"
                  }`}
                  title={!isHovered ? link.name : undefined}
                >
                  <div
                    className={`relative text-xl flex-shrink-0 ${
                      isHovered ? "mr-3" : "mr-0"
                    } ${isActive ? "text-[#0071A5]" : "text-[#0071A5]/50"}`}
                  >
                    {link.icon}
                    {/* Collapsed sidebar: a dot marks chat activity — red for
                        unread replies, amber for sent-but-unopened messages. */}
                    {!isHovered && (link.badge ?? 0) > 0 && (
                      <span
                        title={link.badgeTitle}
                        className={`absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full ${
                          link.badgeColor === "amber"
                            ? "bg-amber-500"
                            : "bg-red-600"
                        }`}
                      />
                    )}
                  </div>
                  {isHovered && (
                    <>
                      <span className="text-sm">{link.name}</span>
                      {(link.badge ?? 0) > 0 && (
                        <span
                          title={link.badgeTitle}
                          className={`ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white ${
                            link.badgeColor === "amber"
                              ? "bg-amber-500"
                              : "bg-red-600"
                          }`}
                        >
                          {(link.badge ?? 0) > 9 ? "9+" : link.badge}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
        <div>
          <Divider className="bg-[#0071A5]/10 h-0.5 my-4" />
          <div
            className={`flex items-center mt-2 w-full ${
              isHovered ? "justify-between gap-2 pl-2" : "justify-center"
            }`}
          >
            {isHovered ? (
              <>
                <div className="flex gap-3 items-center font-bold text-[#0071A5] min-w-0">
                  <Avatar
                    name={
                      currentUser?.name
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("") || "U"
                    }
                    className="bg-[#0071A5]/10 text-[#0071A5] font-medium flex-shrink-0"
                    size="sm"
                  />
                  <span className="text-sm text-[#0071A5] truncate max-w-[100px]">
                    {currentUser?.name || "User"}
                  </span>
                </div>
                <Button
                  className="w-auto !p-0 flex items-center gap-2 bg-transparent hover:border-transparent border-transparent text-sm !text-[#0071A5]/60 font-medium flex-shrink-0"
                  onPress={() => setIsLogoutOpen(true)}
                >
                  Logout
                  <ArrowRightEndOnRectangleIcon className="w-5 h-5 text-[#0071A5]/60" />
                </Button>
              </>
            ) : (
              <Button
                className="w-auto !p-2 bg-transparent hover:border-transparent border-transparent"
                onPress={() => setIsLogoutOpen(true)}
                title="Logout"
              >
                <ArrowRightEndOnRectangleIcon className="w-5 h-5 text-[#0071A5]/60" />
              </Button>
            )}
          </div>
        </div>
      </div>
      <ConfirmationModal isOpen={isLogoutOpen} onOpenChange={setIsLogoutOpen}>
        <div className="text-lg font-semibold mb-3">
          Are you Sure want to Logout?
        </div>
        <div className="flex justify-end gap-2">
          <Button onPress={() => setIsLogoutOpen(false)}>No</Button>
          <Button
            variant="solid"
            className="bg-grayColor text-white"
            onPress={() => logout()}
          >
            Yes
          </Button>
        </div>
      </ConfirmationModal>
    </>
  );
};

export default CohortSidebar;
